/**
 * RecipientBanner.tsx
 *
 * Identity-confirmation strip shown at the top of every magic-link form
 * page. Purpose: catch the "accidentally forwarded email" failure mode —
 * if the wrong person opens a link (autocomplete typo on To:, Reply All
 * gone wrong, helpful colleague clicking on someone else's behalf), the
 * banner makes it obvious whose form this is so they tab away instead
 * of filling it in.
 *
 * Design choices:
 *
 * • EMAIL IS MASKED. We show `r***@rascalrepublic.com` rather than the
 *   full address. Enough to identify ("yes, that's me") without leaking
 *   the recipient's full address to anyone who happens to peek at the
 *   screen (shoulder-surfing in a coworking space, screenshare on a
 *   Zoom call, screenshot in a Slack thread).
 *
 * • RECIPIENT NAME IS NOT MASKED — names are far less sensitive than
 *   email addresses and the name is the strongest "this is mine /
 *   this isn't mine" signal.
 *
 * • "Not you?" LINK is intentional friction for the bystander. Clicking
 *   it doesn't actually revoke the token (we don't trust the bystander
 *   to do that authoritatively); it just opens a `mailto:` so the
 *   bystander can flag HR. The real revoke path stays HR-side.
 *
 * This is Lapis 1 of the accident-forward protection plan. Lapis 2
 * (submit-time identity confirm) and Lapis 4 (OTP on submit for
 * appraisal_sign) layer on top when needed — see firestore.rules +
 * magicLinkOtp.ts when those land.
 */

import React from 'react';

export interface RecipientBannerProps {
  recipientEmail: string;
  recipientName?: string;
  /** Where to send "Not you?" reports. Defaults to hr@rascalrepublic.com. */
  reportTo?: string;
}

/**
 * Masks an email so the user can recognise it without exposing the full
 * address. Rules:
 *   - First char of local part is kept; the rest is replaced with ***
 *   - Domain is shown verbatim (the domain is usually @rascalrepublic.com
 *     anyway and isn't sensitive)
 *   - Inputs that don't look like an email are returned as `***`
 *     (defensive — never leak something we can't safely mask)
 */
export function maskEmail(email: string): string {
  const at = email.indexOf('@');
  if (at <= 0 || at === email.length - 1) return '***';
  const local  = email.slice(0, at);
  const domain = email.slice(at);  // includes the '@'
  if (local.length <= 1) return `${local}***${domain}`;
  return `${local[0]}***${domain}`;
}

const RecipientBanner: React.FC<RecipientBannerProps> = ({
  recipientEmail,
  recipientName,
  reportTo = 'hr@rascalrepublic.com',
}) => {
  const masked = maskEmail(recipientEmail);
  const displayName = recipientName?.trim() || masked;
  const subject = encodeURIComponent(`Magic-link sent to wrong recipient — ${displayName}`);
  const body = encodeURIComponent(
    `Hi HR team,\n\n` +
    `I received a magic link that doesn't appear to be intended for me.\n` +
    `Listed recipient: ${displayName} (${masked}).\n` +
    `Please review and revoke this link if needed.\n`,
  );
  const reportHref = `mailto:${reportTo}?subject=${subject}&body=${body}`;

  return (
    <div
      role="status"
      aria-label="Recipient confirmation"
      className="mb-4 rounded-lg border border-stone-200 bg-stone-50 px-4 py-3"
    >
      <div className="flex items-start gap-3">
        <span
          className="material-symbols-rounded text-stone-500 mt-0.5 flex-shrink-0"
          style={{ fontSize: 20 }}
          aria-hidden="true"
        >
          mail
        </span>
        <div className="min-w-0 flex-1 text-sm text-stone-700">
          <div>
            You are filling this form as{' '}
            <span className="font-semibold text-stone-900">{displayName}</span>
            {' '}
            <span className="text-stone-500">({masked})</span>.
          </div>
          <div className="mt-1 text-xs text-stone-500">
            Not you? Please stop and{' '}
            <a
              href={reportHref}
              className="text-blue-700 hover:text-blue-900 underline"
            >
              report to HR
            </a>
            .
          </div>
        </div>
      </div>
    </div>
  );
};

export default RecipientBanner;
